import React, { Suspense } from "react";
import {
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { PrivateRoute } from "@/components/routing/PrivateRoute";
import { useGetQueryData } from "@/utils/react-query-hooks/general";
import RouteSomeAccessToContentType from "@/components/routing/RouteSomeAccessToContentType";
import RouteAccessOneContent from "@/components/routing/RouteAccessOneContent";
import { ConditionalAccessWrapper } from "@/components/routing/ConditionalAccessWrapper";
import RouteAccessModule from "@/components/routing/RouteAccessModule";
import RouteAccessSomeContent from "@/components/routing/RouteAccessSomeContent";
import GlobalSearch from "@/components/discovery/GlobalSearch";
import PrimarySidebarNav from "@/components/nav/PrimarySidebarNav";
import { useLogout } from "@/utils/react-query-hooks/auth";
import { LoaderCircleV2 } from "oolib";
import ErrorBoundary from "@/components/ErrorBoundary";
import { PeriodSelectionProvider } from "@/components/analysisModules/TopicClusteringTreeViewV2/periodSelection";

// Public "share for all to view" report page — unauthenticated. Registered here too
// because self-managed tenants use this router, not App.jsx's <Routes>.
const PublicReport = React.lazy(() => import("@/pageTpls/PublicReport"));

// Lazy imports
const SM_PlatformContextEditor = React.lazy(() =>
  import("../../_self_managed/SM_PlatformContextEditor")
);
const SM_Onboarding = React.lazy(() =>
  import("../../_self_managed/SM_Onboarding")
);
const SM_ThemesManager = React.lazy(() =>
  import("../../_self_managed/SM_ThemesManager")
);
const SM_UserManagement = React.lazy(() =>
  import("../../_self_managed/SM_UserManagement")
);
const SM_AIEngineManager = React.lazy(() =>
  import("../../_self_managed/SM_AIEngineManager")
);
const SM_PlatformSettings = React.lazy(() =>
  import("../../_self_managed/SM_PlatformSettings")
);
const SM_Integrations = React.lazy(() =>
  import("../../_self_managed/SM_Integrations")
);
const SM_Home = React.lazy(() => import("../../_self_managed/SM_Home"));
const SM_Dashboard = React.lazy(() => import("../../_self_managed/SM_Dashboard"));
const SM_Priorities = React.lazy(() => import("../../_self_managed/SM_Priorities"));
const SM_Topics = React.lazy(() => import("../../_self_managed/SM_Topics"));
const SM_DataImport = React.lazy(() =>
  import("../../_self_managed/SM_DataImport")
);
const SM_TCI = React.lazy(() => import("../../_self_managed/SM_TCI"));
const SM_PublishedListing = React.lazy(() =>
  import("../../_self_managed/SM_PublishedListing")
);
const SM_PublishedPage = React.lazy(() =>
  import("../../_self_managed/SM_PublishedPage")
);
const SM_ContentLanding = React.lazy(() =>
  import("../../_self_managed/SM_ContentLanding")
);
const Datasets = React.lazy(() => import("../../_self_managed/Datasets"));
const DirectDataImport = React.lazy(() =>
  import("../../_self_managed/DirectDataImport")
);
const SM_Analysis = React.lazy(() => import("../../_self_managed/SM_Analysis"));
const TagsManagerV2 = React.lazy(() =>
  import("../../pageTpls/PlatformBuilder/subModules/TagsManagerV2")
);
const AiChat = React.lazy(() => import("../../components/AiChat"));
const Login = React.lazy(() => import("../../pageTpls/Login"));
const Signup = React.lazy(() => import("../../pageTpls/Signup"));
const SelfServeSurvey = React.lazy(() => import("../../pageTpls/SelfServeSurvey"));
const ForgotPassword = React.lazy(() =>
  import("../../pageTpls/auth/ForgotPassword")
);
const ResetPassword = React.lazy(() =>
  import("../../pageTpls/auth/ResetPassword")
);
const GoogleAuth = React.lazy(() => import("../../pageTpls/GoogleAuth"));
const ContributeTpl = React.lazy(() => import("../../pageTpls/ContributeTpl"));
const MyContent = React.lazy(() => import("../../components/profile/MyContent"));
const ModQueue = React.lazy(() => import("../../pageTpls/ModQueue"));
const OKE404 = React.lazy(() => import("../../pageTpls/OKE404"));
const Playground = React.lazy(() => import("../../pageTpls/Playground"));

const filterNavByFeatureFlags = (navConfig, disabledFeatures) => {
  if (!disabledFeatures.length) return navConfig;
  const filterItems = (items) =>
    items
      .map((item) => {
        if (item.sectionId && item.menuItems) {
          const filtered = item.menuItems.filter(
            (mi) => !mi.featureFlag || !disabledFeatures.includes(mi.featureFlag)
          );
          return filtered.length ? { ...item, menuItems: filtered } : null;
        }
        return !item.featureFlag || !disabledFeatures.includes(item.featureFlag)
          ? item
          : null;
      })
      .filter(Boolean);
  return {
    ...navConfig,
    topMenuItems: filterItems(navConfig.topMenuItems || []),
    bottomMenuItems: filterItems(navConfig.bottomMenuItems || []),
  };
};

export const SelfManagedRoutesRenderer = ({
  thisUser,
  SELF_MANAGED_BASE_CONFIGS,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout } = useLogout();
  const platformConfigs = useGetQueryData("platformConfigs");

  const isProd = import.meta.env.VITE_ENV === "prod";
  const disabledFeatures = isProd ? (platformConfigs?.FEATURES_DISABLED_ON_PROD || []) : [];
  const sidebarConfig = filterNavByFeatureFlags(
    SELF_MANAGED_BASE_CONFIGS.primarySidebarNavConfig,
    disabledFeatures
  );

  const routesToRender = (
    <ErrorBoundary>
    <Suspense fallback={<LoaderCircleV2 blockHeight={`calc(100vh)`} />}>
    {/* The period selection ("x vs y") the Overview, Topics and Priorities
        share — above the routes, so moving between them keeps it. */}
    <PeriodSelectionProvider>
      <Routes>
        <Route path="/onboarding" element={<SM_Onboarding />} />
        <Route path="/test-onboarding" element={<SM_Onboarding testMode />} />
        <Route path="/login" element={<Login />} />
        <Route path="/signup" element={<Signup />} />
        <Route
          path="/self-serve-survey"
          element={
            <PrivateRoute>
              <SelfServeSurvey />
            </PrivateRoute>
          }
        />
        <Route
          path="/self-serve-quick-link/:contentType"
          element={
            <PrivateRoute>
              <SelfServeSurvey key="quick-link" />
            </PrivateRoute>
          }
        />
        <Route
          path="/reset-password"
          element={
            <PrivateRoute>
              <ResetPassword />
            </PrivateRoute>
          }
        />
        <Route path="/googleAuth/:token" element={<GoogleAuth />} />
        <Route path="/verifyResetPasswordMail" element={<ForgotPassword />} />
        <Route
          path="/verifyResetPasswordMail/:token"
          element={<ForgotPassword />}
        />
        <Route
          path="/"
          element={
            <PrivateRoute>
              <SM_Home />
            </PrivateRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <PrivateRoute>
              <SM_Dashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/priorities"
          element={
            <PrivateRoute>
              <SM_Priorities />
            </PrivateRoute>
          }
        />
        <Route
          path="/topics"
          element={
            <PrivateRoute>
              <SM_Topics />
            </PrivateRoute>
          }
        />
        <Route
          path="/datasets"
          element={
            <PrivateRoute>
              <Datasets />
            </PrivateRoute>
          }
        />
        <Route
          path="/datasets/direct-data-import"
          element={
            <RouteAccessModule
              moduleId={"templateBuilder"}
              ACTION={["PUBLISH"]}
            >
              <DirectDataImport />
            </RouteAccessModule>
          }
        />
        <Route
          path="/datasets/:contentType"
          element={
            <RouteSomeAccessToContentType ACTION={["READ"]}>
              <SM_PublishedListing />
            </RouteSomeAccessToContentType>
          }
        />
        <Route
          path="/datasets/createTemplate/:contentType"
          element={
            <RouteAccessModule
              moduleId={"templateBuilder"}
              ACTION={["PUBLISH", "DELETE"]}
            >
              <SM_TCI />
            </RouteAccessModule>
          }
        />
        <Route
          path="/datasets/viewTemplate/:contentType"
          element={
            <RouteSomeAccessToContentType ACTION={["READ"]}>
              <SM_TCI />
            </RouteSomeAccessToContentType>
          }
        />

        <Route
          path="/datasets/import/:contentType"
          element={
            <RouteSomeAccessToContentType ACTION={["PUBLISH"]}>
              {/* <SM_DataImport /> */}
              <DirectDataImport isImportMoreDataMode />
            </RouteSomeAccessToContentType>
          }
        />
        <Route
          path="/datasets/:contentType/:contentId"
          element={
            <RouteSomeAccessToContentType ACTION={["READ"]}>
              <SM_PublishedPage />
            </RouteSomeAccessToContentType>
          }
        />
        <Route
          path="/dev/datasets/:contentType/:contentId"
          element={
            <RouteSomeAccessToContentType ACTION={["READ"]}>
              <SM_ContentLanding />
            </RouteSomeAccessToContentType>
          }
        />
        <Route
          path="/datasets/:contentType/:contentId/:edit_moderate"
          element={
            <ConditionalAccessWrapper
              param="edit_moderate"
              paramMap={{
                edit: {
                  comp: "RouteSomeAccessToContentType",
                  props: {
                    ACTION: ["UPDATE", "PUBLISH", "SUBMIT"],
                  },
                },
                moderate: {
                  comp: "RouteAccessOneContent",
                  props: {
                    ACTION: "MODERATE",
                  },
                },
              }}
            >
              <ContributeTpl />
            </ConditionalAccessWrapper>
          }
        />
        {/* Redirect legacy themes-explorer URL to highlights */}
        <Route
          path="/analysis/themes-explorer"
          element={<Navigate to="/analysis/highlights" replace />}
        />
        <Route
          path="/analysis/:analysisType"
          element={
            <PrivateRoute>
              <SM_Analysis />
            </PrivateRoute>
          }
        />
        <Route
          path="/analysis/:analysisType/:reportId/:mode"
          element={
            <PrivateRoute>
              <SM_Analysis />
            </PrivateRoute>
          }
        />
        <Route
          path="/themes-manager"
          element={
            <RouteSomeAccessToContentType
              contentTypes={["themes", "subThemes", "tags"]}
              ACTION={"READ"}
            >
              <SM_ThemesManager />
            </RouteSomeAccessToContentType>
          }
        />
        <Route
          path="/my-content"
          element={
            <PrivateRoute>
              <MyContent />
            </PrivateRoute>
          }
        />
        <Route
          path="/review-dashboard"
          element={
            <RouteAccessSomeContent ACTION="MODERATE">
              <ModQueue />
            </RouteAccessSomeContent>
          }
        />
        <Route
          path="/ai-chat"
          element={
            <RouteAccessModule moduleId={"aiChat"} ACTION={"READ"}>
              <AiChat />
            </RouteAccessModule>
          }
        />
        <Route
          path="/global-search"
          element={
            <RouteAccessSomeContent ACTION="READ">
              <GlobalSearch />
            </RouteAccessSomeContent>
          }
        />
        <Route
          path="/integrations"
          element={
            <RouteAccessModule moduleId="integrations" ACTION="READ">
              <SM_Integrations />
            </RouteAccessModule>
          }
        />
        <Route
          path="/platform-settings/:view"
          element={
            <PrivateRoute>
              <SM_PlatformSettings />
            </PrivateRoute>
          }
        />
        <Route
          path="/platform-settings/:view/:contentType"
          element={
            <PrivateRoute>
              <SM_PlatformSettings />
            </PrivateRoute>
          }
        />
         <Route
          path="/playground"
          element={
            <PrivateRoute>
              <Playground />
            </PrivateRoute>
          }
          />
        {/* PUBLIC "share for all to view" — no auth guard. Must precede the catch-all. */}
        <Route
          path="/public/report/:token"
          element={<PublicReport />}
        />
        <Route
          path="*"
          element={
            <PrivateRoute>
              <OKE404 />
            </PrivateRoute>
          }
        />
      </Routes>
    </PeriodSelectionProvider>
    </Suspense>
    </ErrorBoundary>
  );

  return !thisUser || location.pathname === "/onboarding" || location.pathname === "/test-onboarding" || location.pathname === "/reset-password" || location.pathname === "/self-serve-survey" || location.pathname.startsWith("/self-serve-quick-link") || location.pathname.startsWith("/public/report") ? (
    <div>{routesToRender}</div>
  ) : (
    <PrimarySidebarNav
      gridHeightCSS="100vh"
      lsbId={"SelfManagedShell"}
      lsbWidth={200}
      config={sidebarConfig}
    >
      {routesToRender}
    </PrimarySidebarNav>
  );
};
